/**
 * Monthly Cashflow Report Workflow
 *
 * This workflow generates monthly financial reports:
 * 1. Runs on the first day of each month
 * 2. Aggregates income and expenses from previous month
 * 3. Analyzes trends compared to previous months
 * 4. Generates AI summary with insights
 * 5. Sends comprehensive report to user
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const monthlyCashflowReportWorkflow: Workflow = {
  id: 'monthly-cashflow-report',
  name: 'Report Mensile Cashflow',
  description: 'Genera report mensile con analisi delle entrate/uscite e trend finanziari',
  trigger: {
    type: 'schedule',
    config: {
      // Run on first day of month at 8 AM
      cron: '0 8 1 * *',
      timezone: 'Europe/Rome',
    },
  },
  steps: [
    {
      id: 'gather_data',
      name: 'Raccogli Dati',
      type: 'custom',
      config: {
        // Aggregate previous month's data
        period: 'previous_month',
        includeIncome: true,
        includeExpenses: true,
        includeInvoices: true,
        includeMovements: true,
        // Compare with same period last year
        includeYearOverYear: true,
      },
      next: ['analyze_trends'],
    },
    {
      id: 'analyze_trends',
      name: 'Analizza Trend',
      type: 'ai_analyze',
      config: {
        prompt: `Analyze this month's financial data and identify key trends.

Data provided:
- Total income: {income}
- Total expenses: {expenses}
- Net cashflow: {netCashflow}
- Comparison to previous month: {comparison}
- Year-over-year change: {yoyChange}

Identify:
- Significant changes vs previous period
- Spending patterns or anomalies
- Revenue trends
- Potential concerns or opportunities
- Seasonal factors

Respond in JSON:
{
  "trends": [
    {
      "category": "income|expenses|specific_category",
      "change": "+X%",
      "significance": "high|medium|low",
      "description": "What changed and why it matters"
    }
  ],
  "concerns": ["concern 1", "concern 2"],
  "opportunities": ["opportunity 1", "opportunity 2"]
}`,
      },
      next: ['generate_report'],
    },
    {
      id: 'generate_report',
      name: 'Genera Report',
      type: 'ai_summarize',
      config: {
        prompt: `Generate a comprehensive monthly cashflow report.

Include:
1. Executive summary (2-3 sentences)
2. Key metrics (income, expenses, net)
3. Notable trends and changes
4. Recommendations for next month
5. Action items if any

Keep it concise and actionable. Use Italian.`,
        maxTokens: 800,
      },
      next: ['send_notification'],
    },
    {
      id: 'send_notification',
      name: 'Invia Report',
      type: 'send_notification',
      config: {
        type: 'in_app',
        message: 'Report cashflow per {month} pronto! Net: {netCashflow}€ ({change} vs mese scorso)',
        includeFullReport: true,
        priority: 'high',
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Calculate month-over-month percentage change
 */
export function calculatePercentageChange(current: number, previous: number): string {
  if (previous === 0) return current > 0 ? '+∞%' : '0%';

  const change = ((current - previous) / previous) * 100;
  const sign = change > 0 ? '+' : '';
  return `${sign}${change.toFixed(1)}%`;
}
